<!--
  AccountNotifications.svelte — Notification centre for the current user.

  Modern card-based feed with type-coloured accent bars, inline actions,
  and smooth transitions.  Accessed from Settings > Notifications or by
  clicking the AbstractIDCard when unread notifications exist.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Button, Spinner } from 'flowbite-svelte';
	import RelativeTime from '../../components/RelativeTime.svelte';
	import {
		listNotifications,
		markNotificationRead,
		markAllNotificationsRead,
		deleteNotification
	} from '../../AppNetwork';
	import { unreadNotificationCount } from '../../Auth';
	import { toastError } from '../../toast';
	import type { UserNotification } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	/** All loaded notifications. */
	let notifications: UserNotification[] = $state([]);
	/** Total count for pagination. */
	let total = $state(0);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error banner text. */
	let error = $state('');
	/** Current page (0-indexed). */
	let page = $state(0);
	/** Items per page. */
	const limit = 25;

	/** Fetch notifications from the API. */
	async function loadNotifications(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch the current page of notifications from the API and update local state. */
		try {
			const result = await listNotifications({ limit, offset: page * limit });
			notifications = result.items;
			total = result.total;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load notifications');
		} finally {
			loading = false;
		}
	}

	/** Sync the shared unread count store after any local change. */
	function syncUnreadStore(): void {
		unreadNotificationCount.set(notifications.filter((n) => !n.isRead).length);
	}

	/** Mark a single notification as read. */
	async function handleMarkRead(id: number): Promise<void> {
		try {
			await markNotificationRead(id);
			const n = notifications.find((x) => x.id === id);
			if (n) {
				n.isRead = true;
				n.readAt = new Date().toISOString();
			}
			notifications = notifications;
			syncUnreadStore();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to mark as read'));
		}
	}

	/** Mark all notifications as read. */
	async function handleMarkAllRead(): Promise<void> {
		try {
			await markAllNotificationsRead();
			notifications = notifications.map((n) => ({
				...n,
				isRead: true,
				readAt: n.readAt || new Date().toISOString()
			}));
			syncUnreadStore();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to mark all as read'));
		}
	}

	/** Delete a notification. */
	async function handleDelete(id: number): Promise<void> {
		try {
			await deleteNotification(id);
			notifications = notifications.filter((n) => n.id !== id);
			total--;
			syncUnreadStore();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete notification'));
		}
	}

	/** Accent colour class per notification type. */
	const typeAccent: Record<string, string> = {
		info: 'border-l-blue-500',
		warning: 'border-l-yellow-400',
		error: 'border-l-red-500',
		success: 'border-l-green-500'
	};

	/** Icon per notification type. */
	const typeIcons: Record<string, string> = {
		info: 'fa-circle-info text-blue-500',
		warning: 'fa-triangle-exclamation text-yellow-500',
		error: 'fa-circle-xmark text-red-500',
		success: 'fa-circle-check text-green-500'
	};

	/** Background tint for the type icon area. */
	const typeBg: Record<string, string> = {
		info: 'bg-blue-50 dark:bg-blue-900/20',
		warning: 'bg-yellow-50 dark:bg-yellow-900/20',
		error: 'bg-red-50 dark:bg-red-900/20',
		success: 'bg-green-50 dark:bg-green-900/20'
	};

	let totalPages = $derived(Math.ceil(total / limit));
	let unreadCount = $derived(notifications.filter((n) => !n.isRead).length);

	onMount(loadNotifications);
</script>

<!-- Header -->
<div class="mb-6 flex items-center justify-between">
	<div>
		<h2 class="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
			<i class="fa-solid fa-bell text-primary-500"></i>
			{$t("Notifications")}
			{#if unreadCount > 0}
				<span
					class="inline-flex animate-pulse items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white"
				>
					{unreadCount}
				</span>
			{/if}
		</h2>
		<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{$t("Stay up to date with your account activity")}</p>
	</div>
	{#if unreadCount > 0}
		<Button size="sm" color="light" onclick={handleMarkAllRead} class="shrink-0">
			<i class="fa-solid fa-check-double me-1.5 text-green-500"></i>{$t("Mark all as read")}
		</Button>
	{/if}
</div>

<!-- Error -->
{#if error}
	<div
		class="mb-4 flex items-center gap-2 rounded-xl bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300"
	>
		<i class="fa-solid fa-exclamation-triangle"></i>
		<span>{error}</span>
	</div>
{/if}

<!-- Loading -->
{#if loading}
	<div class="flex flex-col items-center justify-center py-20 text-gray-400">
		<Spinner size="10" />
		<p class="mt-4 text-sm">{$t("Loading notifications…")}</p>
	</div>

	<!-- Empty state -->
{:else if notifications.length === 0}
	<div class="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
		<div class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
			<i class="fa-solid fa-bell-slash text-3xl"></i>
		</div>
		<p class="text-lg font-medium text-gray-600 dark:text-gray-300">{$t("All caught up!")}</p>
		<p class="mt-1 text-sm">{$t("You have no notifications right now.")}</p>
	</div>

	<!-- Notification feed -->
{:else}
	<div class="space-y-3">
		{#each notifications as n (n.id)}
			<div
				class="group relative rounded-xl border-l-4 {typeAccent[n.notificationType] || 'border-l-gray-400'}
					bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-gray-800
					{n.isRead ? 'opacity-60 hover:opacity-90' : ''}"
			>
				<div class="flex items-start gap-4 p-4">
					<!-- Type icon -->
					<div
						class="h-10 w-10 shrink-0 rounded-full {typeBg[n.notificationType] ||
							'bg-depth'} flex items-center justify-center"
					>
						<i class="fa-solid {typeIcons[n.notificationType] || 'fa-bell text-gray-400'} text-lg"></i>
					</div>

					<!-- Content -->
					<div class="min-w-0 flex-1">
						<div class="flex items-start justify-between gap-2">
							<div class="min-w-0">
								<p class="font-semibold leading-snug text-gray-900 dark:text-white">
									{n.title}
									{#if !n.isRead}
										<span class="ml-1.5 inline-block h-2 w-2 rounded-full bg-blue-500 align-middle"
										></span>
									{/if}
								</p>
								{#if n.message}
									<p class="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
										{n.message}
									</p>
								{/if}
								{#if n.actionUrl}
									<a
										href={n.actionUrl}
										class="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
										target="_blank"
										rel="noreferrer"
									>
										{$t("View details")} <i class="fa-solid fa-arrow-right text-[10px]"></i>
									</a>
								{/if}
							</div>

							<!-- Actions — visible on hover / always on mobile -->
							<div
								class="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 sm:opacity-100 sm:group-hover:opacity-100"
							>
								{#if !n.isRead}
									<button
										class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-green-50 hover:text-green-500 dark:hover:bg-green-900/20"
										onclick={() => handleMarkRead(n.id)}
										title={$t("Mark as read")}
									>
										<i class="fa-solid fa-check text-sm"></i>
									</button>
								{/if}
								<button
									class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
									onclick={() => handleDelete(n.id)}
									title={$t("Delete")}
								>
									<i class="fa-solid fa-trash-can text-sm"></i>
								</button>
							</div>
						</div>

						<!-- Timestamp -->
						<p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
							<i class="fa-regular fa-clock me-1"></i><RelativeTime date={n.created} />
						</p>
					</div>
				</div>
			</div>
		{/each}
	</div>

	<!-- Pagination -->
	{#if totalPages > 1}
		<div class="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
			<p class="text-sm text-gray-500 dark:text-gray-400">
				{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
			</p>
			<div class="flex gap-2">
				<Button
					size="sm"
					color="light"
					disabled={page === 0}
					onclick={() => {
						page--;
						loadNotifications();
					}}
				>
					<i class="fa-solid fa-chevron-left me-1"></i>{$t("Prev")}
				</Button>
				<Button
					size="sm"
					color="light"
					disabled={page >= totalPages - 1}
					onclick={() => {
						page++;
						loadNotifications();
					}}
				>
					{$t("Next")}<i class="fa-solid fa-chevron-right ms-1"></i>
				</Button>
			</div>
		</div>
	{/if}
{/if}
